import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import { useT } from '../../i18n/LocaleContext';
import { formatDateInput } from '../../utils/format';

const STATUSES = ['active', 'pending', 'inactive'];
const GENDERS = ['male', 'female'];
const MARITAL_STATUSES = ['single', 'married', 'widowed', 'divorced', 'religious'];
// Rwandan national IDs are 16 digits, but passports are accepted too.
const NATIONAL_ID_PATTERN = /^[A-Za-z0-9]{6,20}$/;

const emptyForm = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  address: '',
  joinedAt: '',
  status: 'pending',
  notes: '',
  parish: '',
  gender: '',
  maritalStatus: '',
  nationalId: '',
  dateOfBirth: '',
};

export default function MemberFormModal({ isOpen, onClose, onSubmit, initial }) {
  const t = useT();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setForm({
        firstName: initial.firstName || '',
        lastName: initial.lastName || '',
        phone: initial.phone || '',
        email: initial.email || '',
        address: initial.address || '',
        joinedAt: formatDateInput(initial.joinedAt) || formatDateInput(new Date()),
        status: initial.status || 'pending',
        notes: initial.notes || '',
        parish: initial.parish || '',
        gender: initial.gender || '',
        maritalStatus: initial.maritalStatus || '',
        nationalId: initial.nationalId || '',
        dateOfBirth: formatDateInput(initial.dateOfBirth),
      });
    } else {
      setForm({ ...emptyForm, joinedAt: formatDateInput(new Date()) });
    }
    setErrors({});
  }, [isOpen, initial]);

  const statusOptions = useMemo(
    () => STATUSES.map((s) => ({ value: s, label: t(`members.statuses.${s}`) })),
    [t]
  );

  const genderOptions = useMemo(
    () => GENDERS.map((g) => ({ value: g, label: t(`members.genders.${g}`) })),
    [t]
  );

  const maritalOptions = useMemo(
    () => MARITAL_STATUSES.map((s) => ({ value: s, label: t(`members.marital_statuses.${s}`) })),
    [t]
  );

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = t('validation.required');
    if (!form.lastName.trim()) next.lastName = t('validation.required');
    if (!form.phone.trim()) next.phone = t('validation.required');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = t('validation.invalid_email');
    }
    const nationalId = form.nationalId.replace(/\s+/g, '');
    if (nationalId && !NATIONAL_ID_PATTERN.test(nationalId)) {
      next.nationalId = t('members.national_id_invalid');
    }
    if (form.dateOfBirth && form.dateOfBirth >= formatDateInput(new Date())) {
      next.dateOfBirth = t('members.dob_in_past');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({ ...form, parish: form.parish.trim(), nationalId: form.nationalId.replace(/\s+/g, '') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? t('members.edit_member') : t('members.new_member')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row form-row--2">
          <FormField label={t('members.field_first_name')} htmlFor="firstName" required error={errors.firstName}>
            <Input id="firstName" value={form.firstName} onChange={set('firstName')} invalid={Boolean(errors.firstName)} autoFocus />
          </FormField>
          <FormField label={t('members.field_last_name')} htmlFor="lastName" required error={errors.lastName}>
            <Input id="lastName" value={form.lastName} onChange={set('lastName')} invalid={Boolean(errors.lastName)} />
          </FormField>
        </div>
        <div className="form-row form-row--2">
          <FormField label={t('members.field_phone')} htmlFor="phone" required error={errors.phone}>
            <Input id="phone" value={form.phone} onChange={set('phone')} invalid={Boolean(errors.phone)} placeholder="+250 7..." />
          </FormField>
          <FormField label={t('members.field_email')} htmlFor="email" error={errors.email} hint={t('common.optional')}>
            <Input id="email" type="email" value={form.email} onChange={set('email')} invalid={Boolean(errors.email)} />
          </FormField>
        </div>
        <div className="form-row form-row--2">
          <FormField label={t('members.field_parish')} htmlFor="parish" hint={t('common.optional')}>
            <Input id="parish" value={form.parish} onChange={set('parish')} maxLength={120} />
          </FormField>
          <FormField label={t('members.field_address')} htmlFor="address" hint={t('common.optional')}>
            <Input id="address" value={form.address} onChange={set('address')} />
          </FormField>
        </div>
        <div className="form-row form-row--3">
          <FormField label={t('members.field_gender')} htmlFor="gender">
            <Select id="gender" options={genderOptions} placeholder={t('members.not_specified')} value={form.gender} onChange={set('gender')} />
          </FormField>
          <FormField label={t('members.field_marital_status')} htmlFor="maritalStatus">
            <Select id="maritalStatus" options={maritalOptions} placeholder={t('members.not_specified')} value={form.maritalStatus} onChange={set('maritalStatus')} />
          </FormField>
          <FormField label={t('members.field_date_of_birth')} htmlFor="dateOfBirth" error={errors.dateOfBirth}>
            <Input id="dateOfBirth" type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} invalid={Boolean(errors.dateOfBirth)} max={formatDateInput(new Date())} />
          </FormField>
        </div>
        <FormField label={t('members.field_national_id')} htmlFor="nationalId" error={errors.nationalId} hint={t('members.national_id_hint')}>
          <Input id="nationalId" value={form.nationalId} onChange={set('nationalId')} invalid={Boolean(errors.nationalId)} maxLength={24} placeholder="1 1990 8 0012345 0 12" inputMode="text" />
        </FormField>
        <div className="form-row form-row--2">
          <FormField label={t('members.field_joined_at')} htmlFor="joinedAt">
            <Input id="joinedAt" type="date" value={form.joinedAt} onChange={set('joinedAt')} />
          </FormField>
          <FormField label={t('members.field_status')} htmlFor="status">
            <Select id="status" options={statusOptions} value={form.status} onChange={set('status')} />
          </FormField>
        </div>
        <FormField label={t('members.field_notes')} htmlFor="notes" hint={t('common.optional')}>
          <Textarea id="notes" value={form.notes} onChange={set('notes')} />
        </FormField>
      </form>
    </Modal>
  );
}
